import { apiService } from './api';

export interface UserMessageDto {
  id: number;
  userId: string;
  subject: string;
  subjectFa?: string | null;
  content: string;
  contentFa?: string | null;
  isRead: boolean;
  readAt: string | null;
  sentBy: string | null;
  senderName: string | null;
  senderUsername: string | null; // For admin view - real username
  sentAt: string;
  createdAt: string;
}

export interface UnreadCountDto {
  count: number;
}

export interface CreateMessageDto {
  subject: string;
  subjectFa?: string | null;
  content: string;
  contentFa?: string | null;
  senderName?: string | null;
}

export interface SendMessageDto extends CreateMessageDto {
  userId: string;
}

export interface SendBulkMessageDto extends CreateMessageDto {
  userIds: string[];
}

export interface BulkMessageSendResultDto {
  totalRecipients: number;
  successCount: number;
  failureCount: number;
  failedUserIds?: string[];
  message: string;
}

export interface PaginatedMessagesResponse {
  messages: UserMessageDto[];
  totalCount: number;
  skip: number;
  take: number;
}

export type InboxReadFilter = 'all' | 'read' | 'unread';
export type InboxDateFilter = 'all' | 'week' | 'month' | 'sixMonths';

export interface InboxFilterState {
  read: InboxReadFilter;
  date: InboxDateFilter;
}

export const DEFAULT_INBOX_FILTERS: InboxFilterState = { read: 'all', date: 'all' };

const DAY_MS = 24 * 60 * 60 * 1000;

export function inboxFilterKey(filters: InboxFilterState): string {
  return `${filters.read}-${filters.date}`;
}

export function inboxDateSinceIso(date: InboxDateFilter): string | undefined {
  const now = Date.now();
  switch (date) {
    case 'week':
      return new Date(now - 7 * DAY_MS).toISOString();
    case 'month':
      return new Date(now - 30 * DAY_MS).toISOString();
    case 'sixMonths':
      return new Date(now - 180 * DAY_MS).toISOString();
    default:
      return undefined;
  }
}

function inboxFiltersToQueryParams(filters: InboxFilterState = DEFAULT_INBOX_FILTERS): {
  readStatus?: string;
  since?: string;
} {
  const params: { readStatus?: string; since?: string } = {};
  if (filters.read === 'unread') {
    params.readStatus = 'unread';
  } else if (filters.read === 'read') {
    params.readStatus = 'read';
  }
  const since = inboxDateSinceIso(filters.date);
  if (since) {
    params.since = since;
  }
  return params;
}

/** Coerce API / legacy values to a boolean read flag. */
export function messageIsRead(message: UserMessageDto): boolean {
  const raw = message.isRead as unknown;
  if (raw === true || raw === 1 || raw === 'true' || raw === '1') return true;
  return false;
}

/** Client-side filter fallback (e.g. non-paginated inbox) — mirrors server rules. */
export function applyInboxFiltersClient(messages: UserMessageDto[], filters: InboxFilterState): UserMessageDto[] {
  let result = messages;
  if (filters.read === 'unread') {
    result = result.filter((m) => !messageIsRead(m));
  } else if (filters.read === 'read') {
    result = result.filter((m) => messageIsRead(m));
  }
  const sinceIso = inboxDateSinceIso(filters.date);
  if (sinceIso) {
    const sinceMs = new Date(sinceIso).getTime();
    result = result.filter((m) => new Date(m.sentAt).getTime() >= sinceMs);
  }
  return result;
}

class MessageService {
  /**
   * Get all messages for the current user
   * Always bypasses cache to ensure fresh data from DB
   */
  async getUserMessages(): Promise<UserMessageDto[]> {
    const response = await apiService.get<UserMessageDto[]>('/api/Message/inbox', { cache: false });
    return response;
  }

  /**
   * Get paginated messages for the current user
   * Always bypasses cache to ensure fresh data from DB
   */
  async getUserMessagesPaginated(
    skip: number = 0,
    take: number = 10,
    filters: InboxFilterState = DEFAULT_INBOX_FILTERS
  ): Promise<PaginatedMessagesResponse> {
    const params = new URLSearchParams({ skip: String(skip), take: String(take) });
    const filterParams = inboxFiltersToQueryParams(filters);
    if (filterParams.readStatus) {
      params.set('readStatus', filterParams.readStatus);
    }
    if (filterParams.since) {
      params.set('since', filterParams.since);
    }
    const response = await apiService.get<PaginatedMessagesResponse>(
      `/api/Message/inbox/paginated?${params.toString()}`,
      { cache: false }
    );
    return response;
  }

  /**
   * Get unread message count for the current user
   * Always bypasses cache to ensure fresh data from DB
   */
  async getUnreadCount(): Promise<number> {
    const response = await apiService.get<UnreadCountDto>('/api/Message/unread-count', { cache: false });
    return response.count;
  }

  /**
   * Mark a message as read
   */
  async markAsRead(messageId: number): Promise<void> {
    await apiService.post(`/api/Message/${messageId}/read`, {});
  }

  /**
   * Mark a message as unread
   */
  async markAsUnread(messageId: number): Promise<void> {
    await apiService.post(`/api/Message/${messageId}/unread`, {});
  }

  /**
   * Send a message to a single user (admin only)
   */
  async sendMessage(sendDto: SendMessageDto): Promise<UserMessageDto> {
    const response = await apiService.post<UserMessageDto>('/api/Message/send', sendDto);
    return response;
  }

  /**
   * Send messages to multiple users (admin only)
   */
  async sendBulkMessages(bulkDto: SendBulkMessageDto): Promise<BulkMessageSendResultDto> {
    const response = await apiService.post<BulkMessageSendResultDto>('/api/Message/send-bulk', bulkDto);
    return response;
  }

  /**
   * Get messages for a specific user (admin view)
   * Always bypasses cache to ensure fresh data from DB
   */
  async getUserMessagesForAdmin(userId: string): Promise<UserMessageDto[]> {
    const response = await apiService.get<UserMessageDto[]>(`/api/Message/user/${userId}`, { cache: false });
    return response;
  }

  /**
   * Get unread message count for a specific user (admin only)
   * Always bypasses cache to ensure fresh data from DB
   */
  async getUserUnreadCount(userId: string, bypassCache?: boolean): Promise<number> {
    const url = `/api/Message/user/${userId}/unread-count`;
    // Use bypassCache parameter if provided, otherwise default to false (use cache)
    // This allows initial load to bypass cache while subsequent loads can use cache
    const response = await apiService.get<UnreadCountDto>(url, bypassCache ? { cache: false } : undefined);
    return response.count;
  }
}

export const messageService = new MessageService();
